import type React from 'react';

import { ArrowLeftIcon, ArrowRightIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core-react/i18n';

type ButtonProps = { onClick: () => void; disabled: boolean };
type Props = {
	previous: ButtonProps;
	next: ButtonProps;
	prevFade?: string | string[];
	nextFade?: string | string[];
};

const NAV_BUTTON_CLASS =
	'absolute z-[1] row-start-1 flex h-9 w-9 place-self-center items-center justify-center rounded-full border border-stroke-weaker bg-background-base text-icon-strong shadow-[0px_3px_3px_0px_#E5E5E5] focus:static disabled:cursor-default';

const FADE_CLASS = 'pointer-events-none absolute inset-y-0 w-[50px]';

function toBackground(fade: string | string[] | undefined): string | undefined {
	if (fade === undefined) return undefined;
	return Array.isArray(fade) ? fade[fade.length - 1] : fade;
}

export const NavigationButtons: React.FC<Props> = ({ previous, next, prevFade, nextFade }) => {
	return (
		<div className="relative contents">
			<button
				type="button"
				onClick={previous.onClick}
				aria-disabled={previous.disabled}
				disabled={previous.disabled}
				data-testid="previous-button"
				className={cn(NAV_BUTTON_CLASS, 'col-start-1', previous.disabled ? 'md:absolute' : 'md:static')}
				style={{ left: previous.disabled ? '-1000px' : '0' }}
			>
				<ArrowLeftIcon size={20} aria-hidden />
				<span className="sr-only">
					<Translate value="corgi.components.scroll_container.navigation_btn.previous" />
				</span>
			</button>
			<button
				type="button"
				onClick={next.onClick}
				aria-disabled={next.disabled}
				disabled={next.disabled}
				data-testid="next-button"
				className={cn(NAV_BUTTON_CLASS, 'col-start-3', next.disabled ? 'md:absolute' : 'md:static')}
				style={{ right: next.disabled ? '-1000px' : '0' }}
			>
				<ArrowRightIcon size={20} aria-hidden />
				<span className="sr-only">
					<Translate value="corgi.components.scroll_container.navigation_btn.next" />
				</span>
			</button>
			<div
				aria-hidden
				hidden={previous.disabled}
				className={cn(FADE_CLASS, 'left-0', previous.disabled ? 'hidden' : 'hidden md:block')}
				style={{ background: toBackground(prevFade) }}
			/>
			<div
				aria-hidden
				hidden={next.disabled}
				className={cn(FADE_CLASS, 'right-0', next.disabled ? 'hidden' : 'hidden md:block')}
				style={{ background: toBackground(nextFade) }}
			/>
		</div>
	);
};
